import { theme, type ThemeConfig } from 'antd'

/** The web app's field look (`--color-input-line`, `--color-focus`), shared by every text-like input. */
function fieldTokens(dark: boolean) {
  const focus = dark ? '#a47bff' : '#8648ff'
  return {
    borderRadius: 12,
    borderRadiusLG: 14,
    borderRadiusSM: 8,
    colorBorder: dark ? '#6b757f' : '#929292',
    activeBorderColor: focus,
    hoverBorderColor: focus,
    activeShadow: `0 0 0 2px ${dark ? 'rgb(164 123 255 / 0.25)' : 'rgb(134 72 255 / 0.25)'}`,
  }
}

/** The portal's antd theme: brand tokens, the website's dark surfaces, and the web app's dialogs and fields. */
export function adminTheme(dark: boolean): ThemeConfig {
  return {
    algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: '#8648ff',
      borderRadius: 6,
      // Brand body font (Manrope); headings switch to Bricolage Grotesque in
      // styles/global.css.
      fontFamily: 'var(--font-secondary)',
      // The website's dark surfaces, so both apps feel like one product.
      ...(dark && {
        colorBgLayout: '#0c1014',
        colorBgContainer: '#12171c',
        colorBgElevated: '#1a2026',
        colorBorder: '#2f363d',
        colorBorderSecondary: '#22282e',
        colorLink: '#b598ff',
      }),
    },
    components: {
      // Dialogs in the web app's design language (its "Coming soon" mini-app
      // dialog), with the web's dialog overlay behind. The frosted panel
      // itself (glass, border, blur, padding; antd has no public token for
      // those) is in styles/global.css, so it follows <html data-theme> and
      // also reaches static Modal.confirm calls, which never see this
      // ConfigProvider.
      Modal: {
        borderRadiusLG: 28,
        headerBg: 'transparent',
        footerBg: 'transparent',
        titleFontSize: 20,
        colorBgMask: dark ? 'rgb(0 0 0 / 0.6)' : 'rgb(28 7 68 / 0.45)',
      },
      // Fields as on the web app: larger corners, a border that meets WCAG
      // 1.4.11 (3:1, antd's default is ~1.4:1) and a soft brand-purple ring.
      // Card values large enough to read at a glance (antd's default is 24px),
      // as on the ICT portal's stat cards. Weight and spacing in global.css.
      Statistic: { contentFontSize: 36 },
      Input: fieldTokens(dark),
      InputNumber: fieldTokens(dark),
      DatePicker: fieldTokens(dark),
      Select: {
        ...fieldTokens(dark),
        activeOutlineColor: dark ? 'rgb(164 123 255 / 0.25)' : 'rgb(134 72 255 / 0.25)',
      },
    },
  }
}
